/**
 * Wygląd z makiety (status 28.10.2026 pkt 3, decyzja użytkownika 28.10.2026,
 * przełącznik `mockupLook`, pozycja S-28.10-01) — tokeny systemu wizualnego.
 *
 * Wartości przepisane z makiety p. Mariusza (paczka 28.10:
 * `makiety/ftl-makieta/assets/style-wzorzec.css`, `style-makieta.css`
 * i `makiety/makiety-cs-adm.html` — obie makiety mają te same wartości,
 * zmienne `--text`, `--line`, `--blue`, `--soft`…). Tylko wygląd: kolory,
 * typografia, odstępy, zaokrąglenia. Układu makiety nie przenosimy tam,
 * gdzie ucina tekst — u nas tekst ma się zawsze mieścić.
 */

/** Przełącznik w rejestrze API (`feature-flags.ts`). */
export const MOCKUP_LOOK_FEATURE = 'mockupLook';

export const MOCKUP_TOKENS = {
  /** `--text`, `--text2`, `--line` */
  text: 'rgba(0,0,0,.87)',
  text2: 'rgba(0,0,0,.6)',
  line: 'rgba(0,0,0,.12)',
  /** tło strony (`--bg`), treść i karty (`--panel`), paski podsumowań (`--soft`) */
  pageBg: '#fafafb',
  panel: '#ffffff',
  soft: '#f5f7fa',
  /** `--blue`, `--blue-dark`, pasek górny (`--appbar`), `--info` */
  blue: '#1976d2',
  blueDark: '#1565c0',
  appbar: '#2196f3',
  info: '#0288d1',
  /** podświetlenie wiersza, zaznaczona pozycja menu */
  blueHover: 'rgba(25,118,210,.08)',
  blueSelected: 'rgba(25,118,210,.1)',
  blueOutline: 'rgba(25,118,210,.5)',
  blueLight: '#e3f2fd',
  green: '#2e7d32',
  greenBg: '#edf7ed',
  red: '#d32f2f',
  redBg: '#fdecea',
  /** pomarańcz zarezerwowany dla ostrzeżeń */
  orange: '#ed6c02',
  orangeText: '#c75a00',
  orangeBg: '#fff4e5',
  purple: '#7b1fa2',
  purpleText: '#6a1b9a',
  /** obrys „pigułki” bez koloru */
  chipBorder: '#bdbdbd',
  /** dymek ze szczegółami (`#tip`) */
  tooltipBg: '#263238',
  fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
  fontSize: 14,
  /** nagłówek ekranu (`h1`), tytuł sekcji karty (`.card h2`), podpis wersalikami (`.k`, `.cap`) */
  pageTitleSize: 24,
  sectionTitleSize: 17,
  capsSize: 11,
  capsSpacing: '.6px',
  /** liczba na pasku podsumowania pulpitu (`.summary.kpi .v`) */
  kpiValueSize: 22,
  radius: 4,
  /** znacznik przy tytule sekcji: 4 × 20 px, zaokrąglony */
  markerWidth: 4,
  markerHeight: 20,
  appBarHeight: 48,
} as const;

/**
 * Klasy-zaczepy, którymi wspólne elementy ekranów (karta sekcji, kafelek
 * pulpitu, sekcja okna zlecenia) mówią motywowi „to jest tytuł sekcji”.
 * Bez przełącznika klasa nic nie zmienia — style podpina wyłącznie
 * `MockupLookTheme`.
 */
export const MOCKUP_CLASS = {
  /** tytuł karty sekcji (`SectionCard`, sekcje okna zlecenia) */
  sectionTitle: 'ff-section-title',
  /** kafelek liczby na pulpicie (`Tile`) */
  tile: 'ff-tile',
  /** podpis kafelka (wersaliki) */
  tileLabel: 'ff-tile-label',
  /** liczba na kafelku */
  tileValue: 'ff-tile-value',
  /** tekst z dopiskiem w jednym wierszu listy (`InlinePair`) — przy braku miejsca dopisek schodzi niżej */
  inlinePair: 'ff-inline-pair',
} as const;

/**
 * Partia 2 (29.10.2026): wiersz kolejki z paskiem wagi z lewej, jak `.sev`
 * makiety back office (kolejka CS, awizacje po terminie). Wartość — kolor
 * MUI (`error`, `warning`, `success`, `info`); bez przełącznika atrybut nic
 * nie zmienia.
 */
export const MOCKUP_SEVERITY_ATTR = 'data-ff-severity';
export type MockupSeverity = 'error' | 'warning' | 'success' | 'info';

/** Kolor znacznika MUI → waga paska wiersza; kolor bez wagi (`default`) — bez paska. */
export function mockupSeverityOf(color: string | undefined): MockupSeverity | undefined {
  return color === 'error' || color === 'warning' || color === 'success' || color === 'info' ? color : undefined;
}

/** Atrybut na korzeniu układu, gdy wygląd z makiety jest włączony (e2e, zrzuty). */
export const MOCKUP_LOOK_ATTR = 'data-mockup-look';
